import { create } from 'zustand';
import { io, type Socket } from 'socket.io-client';

import type { MapMode, MapSurface, SceneFeature } from '@portal/shared';

import {
  appendNarrationChunk,
  commitNarration,
  dropLiveNarration,
  sortBySeq,
  type RenderedMessage,
} from './messages.js';
import {
  combatEnded,
  combatStarted,
  combatantsUpdated,
  declarationAcked,
  roundBegan,
  windowClosed,
  windowOpened,
  type CombatView,
  type CombatantView,
} from './combat.js';

/**
 * The live connection to a campaign.
 *
 * Messages render **optimistically**: a player's own line appears the moment
 * they send it, marked pending, and is reconciled when the server's
 * authoritative copy arrives. That matters because the server is the one
 * assigning `seq`, and waiting for a round trip to see your own typing is the
 * difference between a chat that feels live and one that feels laggy.
 *
 * Reconciliation is by `clientId`, not by content — two identical lines are a
 * normal thing to type, and matching on text would collapse them.
 */

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected';

export type { RenderedMessage } from './messages.js';

export interface RosterEntry {
  characterId: string;
  name: string;
  /** Moves mid-play, so it arrives with every roster refresh. */
  isOwner?: boolean;
  /** What happens on this player's behalf while away. Absent = the default. */
  presencePolicy?: string;
  /** Where they are, when that is not with the party. Absent = with it. */
  station?: string;
  /** Public: who died is not a secret, and a spirit is visible at the table. */
  deathStatus?: string;
  characterClass: string;
  level: number;
  hitPoints: { current: number; max: number };
  presence: 'online' | 'idle' | 'away' | 'disconnected';
}

/**
 * What one combat declaration says.
 *
 * Named fields rather than a positional list — see `declareCombat` below for
 * why, and for the class of bug it prevents.
 */
export interface CombatDeclaration {
  readonly action: string;
  readonly targetId?: string | undefined;
  readonly weapon?: string | undefined;
  readonly spell?: string | undefined;
  /**
   * Which of a spell's own printed modes the caster is choosing — Monster
   * Summoning VII's own "several weaker allies now, or one stronger ally two
   * rounds from now" is the rare spell that offers a real choice at the
   * moment of casting. An id, never the display label. Only meaningful
   * alongside `spell`, and only for the handful of spells that offer one.
   */
  readonly spellMode?: string | undefined;
  /** Animal Summoning: the kinds of animal the caster calls, one to three names. */
  readonly spellAnimals?: readonly string[] | undefined;
  /**
   * Casts the spell's printed REVERSE instead — Bless's own "Curse", for
   * now. Only meaningful alongside `spell`, and only for a spell that
   * prints one; refused by name for any other. Absent means the forward
   * cast, the book's own default reading of a spell's name.
   */
  readonly reversed?: boolean | undefined;
  /**
   * Bless's own "optionally, a single item can be blessed" — a weapon the
   * caster is currently carrying. Only meaningful alongside `spell` for a
   * spell that offers it; absent means no item this cast.
   */
  readonly blessedWeapon?: string | undefined;
  /**
   * Who is acting. Absent means yourself — the server's own default. A patron
   * declares for a henchman by naming them, and the server re-checks that the
   * named character really is theirs.
   */
  readonly characterId?: string | undefined;
  /**
   * Which range band the shot is at.
   *
   * The band, never the penalty: the figures are the table's and the client has
   * no business knowing them. Absent means short, which carries none.
   */
  readonly range?: 'short' | 'medium' | 'long' | undefined;
  /**
   * Whether an unarmed attacker is pulling it. Absent means not — the book's
   * baseline, and the server's own default.
   */
  readonly holdingBack?: boolean | undefined;
  /**
   * A feature of the room this character is moving to — "behind the crates".
   *
   * ⚠ `''` is **"step out into the open"**, which is a real declaration and has
   * to stay distinguishable from saying nothing about position at all. Folding
   * the two together makes one of them unexpressible, which is the
   * `waypoint: null` rule.
   */
  readonly feature?: string | undefined;
  /** Where a `move` is headed — a point on the encounter map, not a creature. */
  readonly destination?: { mapX: number; mapY: number } | undefined;
}

/**
 * What the DM is doing, for the one line under the transcript.
 *
 * Every one of these is a state play continues through — the engine has
 * already resolved and persisted whatever happened, so this reports on
 * decoration and never on whether the game is working.
 */
export interface DmStatus {
  typing: boolean;
  /**
   * 1-based place in the narration queue, while waiting behind other tables.
   *
   * Explicitly `| undefined` on all three, so a handler can clear a previous
   * beat's status by assignment rather than by rebuilding the object around
   * the keys it wants to omit.
   */
  queuedAt?: number | undefined;
  degraded?: 'terse' | 'fast_model' | 'coalesced' | undefined;
  unavailable?: 'provider_down' | 'budget_exhausted' | 'stalled' | undefined;
}

interface SocketState {
  state: ConnectionState;
  campaignId: string | null;
  messages: RenderedMessage[];
  roster: RosterEntry[];
  dm: DmStatus;
  /**
   * The world clock, in elapsed rounds — the canonical tick, advanced by
   * events and never by wall-clock time.
   *
   * Day and time of day come from the server: the conversion is a rule — the
   * round length, and a calendar the ruleset explicitly lets a GM vary — so
   * dividing by 60 and 24 here would be quietly right for the default setting
   * and wrong for any other. Bare rounds are the fallback.
   */
  clock: { elapsedRounds: number; day?: number | undefined; timeOfDay?: string | undefined } | null;
  /**
   * The fight, or null out of combat.
   *
   * Assembled from the beats as they arrive and rebuilt wholesale from the
   * snapshot on a reconnect — which is what lets a player rejoin an *open*
   * window rather than stare at an empty one.
   */
  combat: CombatView | null;
  /**
   * The viewer's own character.
   *
   * Read off the snapshot's audience, which the server addresses to exactly
   * one character — the connection's *current* one. Asking the roster instead
   * would not know which of two rerolled characters is live.
   */
  myCharacterId: string | null;
  /** Server-authoritative: the client greys its input, this decides. */
  inputLocked: boolean;
  /**
   * Which surface is on screen. A *view* choice — `combatGrid` implies combat,
   * the other three are picks within exploration.
   *
   * It arrives on the snapshot and on `state:mode`, and it was dropped on the
   * floor for a phase, which is why nothing could render a map.
   */
  mapMode: MapMode;
  /**
   * The images behind the active surface, keys already resolved to paths.
   *
   * Room-wide: everything here is the same for every member. Which *places*
   * this player has seen is per-viewer and comes from `/places` instead.
   */
  map: MapSurface;
  /**
   * The canon place the "you are here" pin renders from.
   *
   * A projection, never a thing anyone drags: it is joined onto the waypoint
   * list at render time, so it cannot drift from where the party actually is.
   */
  partyLocation: string | null;
  /**
   * The story so far — the rolling campaign summary.
   *
   * Room-wide and unfiltered by construction. Shown to a player with no
   * scrollback, which is what a newcomer or a rerolled player has.
   */
  story: string | null;
  /** "What you missed", present only after an actual absence. */
  digest: {
    sinceSeq: number;
    lines: { author: string; text: string }[];
    omitted: number;
    atLeast?: boolean;
  } | null;
  /** Dismisses the catch-up panel. Client-side only; nothing is persisted. */
  dismissDigest: () => void;
  /** Clears the last refusal. A refusal a player has read is done with. */
  clearError: () => void;
  /** Clears a stale claim result before a fresh Stage-0 claim is fired. */
  clearObjectionClaim: () => void;
  /**
   * Map regions the party has uncovered.
   *
   * Only ever grows: what is not here has not been seen, so a region added to
   * the map later is hidden by default rather than visible.
   */
  revealed: string[];
  /** What the party is standing in, as the DM declared it. */
  surroundings: string[];
  /** What is in the scene that a fight can use, named by the DM. */
  sceneFeatures: SceneFeature[];
  /**
   * A skip of half a day or more that can still be taken back.
   *
   * ⚠ On the snapshot, not only on the announcement: the line fires once, and
   * a player who reloads would otherwise have no way to reach the control that
   * the line is telling them about.
   */
  timeSkip: { band: string; label: string } | null;
  /**
   * "Aragorn is searching the desk…", while it resolves.
   *
   * The server sends one only for a *surfaced* attempt; an ambient or
   * concealed one sends nothing at all, so there is nothing here to leak.
   */
  pending: string | null;
  /**
   * The last authoritative change to *your* sheet.
   *
   * Audienced to the owner: the fact of a level-up is public and goes in the
   * transcript, but the numbers are yours. Kept as a note rather than applied,
   * because this page does not render a sheet — the patch is the server's word
   * that one changed.
   */
  sheetNotice: string | null;
  /**
   * The acknowledgement that a veto was heard — **the presser's own screen and
   * nowhere else**.
   *
   * Rendered rather than deferred: the accepted press announces "The scene
   * moves on." to the room, but a press inside the cooldown announces nothing
   * *by design* (a refusal reaching the room would be a second signal, from
   * somebody the table must not be able to identify) — so without this, the
   * second press was complete silence for the one person who needs the button
   * to visibly work.
   */
  vetoNotice: string | null;
  /**
   * The objection the table is currently voting on, if there is one.
   *
   * One at a time by construction, and enforced on the server two ways: a beat
   * can be objected to once ever, and a table can have one objection open at a
   * time. The second is what makes a single slot here honest. Held as state
   * rather than a transient
   * notice because a player who reloads mid-vote must still be able to vote;
   * an event alone would be lost by anyone who reconnected a second later,
   * which is the same reason fog rides on the snapshot.
   */
  openObjection: {
    objectionId: string;
    messageId: string;
    /**
     * `'grounds'` — Stage 2, the appellant's own free-text entry, pending.
     * `'voting'` — Stage 3, the vote itself. **Sustain/Overrule must only be
     * offered in this stage** — voting during `'grounds'` is refused
     * server-side ("that vote has already closed"), a confusing message for
     * a vote that has not started, so the client must not invite it.
     */
    stage: 'grounds' | 'voting';
    appellant: string;
    /** The disputed beat's own text. Absent until Stage 3 (`objection:grounds`). */
    beat?: string;
    reason?: string;
    stageDeadlineTs: number;
    voted?: boolean;
  } | null;
  /** How the last vote came out, for the line the table reads. */
  objectionNotice: string | null;
  /**
   * The result of the most recent Stage-0 claim, for the Stage-1 dialog to
   * show immediately.
   *
   * **A dedicated field, not the generic `lastError`.** A claim refusal has
   * its own inline place to land (the confirm dialog itself, "too late"),
   * where the generic error banner is for actions with nowhere better to
   * put the answer — showing both would be the same message twice.
   */
  objectionClaim: { readonly ok: boolean; readonly reason?: string } | null;
  /**
   * What the table is being asked to agree to, if anything.
   *
   * **State, not a notice** — the same reason `openObjection` is: a player who
   * reloads mid-vote must still be able to vote, and an event alone is lost by
   * anybody who reconnected a second later. What is *being* proposed is said in
   * the room as prose; this only decides whether a control appears.
   */
  openProposal: {
    proposalId: string;
    kind: string;
    subjectCharacterId?: string;
    deadlineTs: number;
    voted?: boolean;
    /** What is being decided, in the sentence the room was told. */
    summary?: string;
  } | null;
  /**
   * Bumped on every `character:update`.
   *
   * A monotonic counter rather than the notice text, because the notice is the
   * same sentence twice for two ordinary changes — "your sheet changed" — so
   * anything watching it for a refresh would miss the second cast of the same
   * round and keep offering a slot the server has already spent.
   */
  sheetVersion: number;
  /** Set when the server refuses something, so the UI can say why. */
  lastError: string | null;

  connect: (campaignId: string) => void;
  disconnect: () => void;
  say: (text: string) => void;
  /**
   * A line only the named characters can read.
   *
   * `chat:whisper` has been in the contract, handled, audience-filtered and
   * *rendered* since Phase 4 — and no client could send one, so the only
   * whisper anybody ever saw was one the engine wrote.
   */
  whisper: (text: string, to: readonly string[]) => void;
  /**
   * Speaks in thieves' cant.
   *
   * **No audience argument.** Who understands cant is a rule — the thief class
   * at this table — and a client-supplied list would be a player choosing
   * their own listeners. The server builds both audiences and writes both
   * lines: the room's "X says something in thieves' cant", and the words.
   */
  speakCant: (text: string) => void;
  /**
   * The content veto. **No actor field, deliberately** — the server never
   * persists or emits who pressed it, so this sends nothing but the campaign.
   */
  raiseVeto: () => void;
  /** Takes the player's own last X-card press back, if it was a few seconds ago. */
  undoVeto: () => void;
  /** Put one of the four judgements to the table. */
  propose: (kind: string, payload?: Record<string, unknown>, subjectCharacterId?: string) => void;
  /** A voice on the open proposal. */
  voteOnProposal: (inFavour: boolean) => void;
  /** Ask the DM for a beat. Any player's — it decides nothing. */
  promptDm: (text: string) => void;
  raiseObjection: (messageId: string) => void;
  /** Stage 0's lock-in — see `claimObjection`'s server-side docstring. */
  claimObjectionLink: (messageId: string) => void;
  /** Stage 2: the appellant's own Grounds, submitted once. */
  submitObjectionGrounds: (objectionId: string, grounds?: string) => void;
  voteOnObjection: (sustain: boolean) => void;
  /**
   * `disposition` is how the party is carrying itself, for a `talk`.
   *
   * **The players', and nobody else's.** Table
   * 10.7 is read against "the PCs' disposition toward the opposing party", and
   * the party is the only one who knows that.
   */
  submitAction: (verb: string, text: string, targetId?: string, disposition?: string) => void;
  /**
   * A combat declaration.
   *
   * ⚠ **One object, not a positional list.** This had grown to four
   * interchangeable `string | undefined` parameters — target, weapon, spell,
   * acting character — and the feature being used would have made five, where
   * a transposition typechecks perfectly and produces a plausible declaration
   * by the wrong character with the wrong weapon. That is the shape
   * `paintTerrain` is kept pure and separate for: a convincing picture of the
   * wrong world.
   */
  declareCombat: (declaration: CombatDeclaration) => void;
  /**
   * Studies or prays for spells. **A top-up, not a reset** — memorised spells
   * persist until cast, so this only fills what casting emptied.
   */
  memoriseSpells: (
    entries: readonly {
      readonly name: string;
      readonly casterKind: 'wizard' | 'priest';
      /**
       * Conjure Elemental's own printed choice, made at memorisation —
       * only ever sent for that one spell. Absent for every other entry.
       */
      readonly elementType?: string;
    }[],
    /** A henchman of the sender's character. Absent means themselves. */
    characterId?: string,
  ) => void;
  /** Casting outside combat. In a fight, casting is a declaration. */
  castSpell: (name: string, targetId?: string, characterId?: string) => void;
  saveNotes: (text: string) => void;
  /**
   * The panel's controls, as one action rather than nine.
   *
   * The server gates every one of them on its own terms — this is a convenience
   * for the panel, not a permission. A caller without the right is refused by
   * name.
   */
  hostCommand: (event: string, payload?: Record<string, unknown>) => void;
  rollDice: (sides: number, count?: number, isPrivate?: boolean) => void;
  /**
   * An ability or skill check. Names one or the other and **nothing numeric**:
   * every modifier lands on the score and the server resolves them.
   */
  rollCheck: (
    what: { skill?: string; ability?: string },
    isBlind?: boolean,
    asGroup?: boolean,
  ) => void;
  setPresence: (state: 'online' | 'idle' | 'away') => void;
}

/**
 * Server-to-client events this store deliberately does not render yet.
 *
 * The mirror of `DIRECTOR_EVENTS` on the server, and for the same reason: an
 * event the contract defines and nobody handles is invisible. The whole
 * narration stream sat in this position for a phase — the server emitted it
 * and the client dropped every chunk on the floor — and nothing failed,
 * because nothing was checking. There is a test that reads the contract and
 * asserts every event is either handled below or named here.
 */
/**
 * Contract events **no control can send yet**, named rather than merely absent.
 *
 * The mirror of `NOT_RENDERED_YET`, and it exists because the same thing had
 * happened in this direction with nothing checking: `player:veto`,
 * `chat:whisper` and
 * `host:roll_reaction` were all in the contract, handled by the server,
 * audience-filtered and tested — and reachable from no button in any client.
 * The veto is the one that mattered: the table's safety tool, complete on the
 * server and impossible to press.
 *
 * Empty is the healthy state. A name here is a decision, not an oversight.
 */
export const NOT_SENDABLE_YET: readonly string[] = [];

/**
 * `veto:ack` used to be the only entry here, deferred because "the
 * transcript already carries it" — true of the *accepted* press, which
 * announces a system line to the room, and not true of a press inside the
 * cooldown, which announces nothing anywhere by design. So the second press
 * was silence for the person who needed the button to visibly work. It is
 * rendered now, to the presser alone.
 *
 * Empty otherwise — `objection:claim_ack` used to sit here for the same
 * reason `player:object_claim` sat in `NOT_SENDABLE_YET`, and both are wired
 * now: `ObjectionConfirmDialog` fires the claim on open and renders this
 * ack's result inline.
 */
export const NOT_RENDERED_YET = [] as const;

let socket: Socket | null = null;

const NO_DM_ACTIVITY: DmStatus = { typing: false };

/** Makes each live engine line unique; they all share one sentinel seq. */
let liveLineCounter = 0;

export const useSocket = create<SocketState>((set, get) => ({
  state: 'idle',
  campaignId: null,
  messages: [],
  roster: [],
  dm: NO_DM_ACTIVITY,
  clock: null,
  mapMode: 'theatre',
  map: {},
  partyLocation: null,
  story: null,
  digest: null,
  revealed: [],
  surroundings: [],
  sceneFeatures: [],
  timeSkip: null,
  sheetNotice: null,
  vetoNotice: null,
  openObjection: null,
  objectionClaim: null,
  openProposal: null,
  objectionNotice: null,
  sheetVersion: 0,
  pending: null,
  combat: null,
  myCharacterId: null,
  inputLocked: false,
  lastError: null,

  dismissDigest: () => set({ digest: null }),
  clearError: () => set({ lastError: null }),
  clearObjectionClaim: () => set({ objectionClaim: null }),

  connect: (campaignId) => {
    if (socket) socket.disconnect();
    set({
      state: 'connecting',
      campaignId,
      messages: [],
      roster: [],
      dm: NO_DM_ACTIVITY,
      clock: null,
      mapMode: 'theatre',
      map: {},
      partyLocation: null,
      story: null,
      digest: null,
      revealed: [],
      sheetNotice: null,
      pending: null,
      combat: null,
      myCharacterId: null,
      inputLocked: false,
      lastError: null,
    });

    socket = io({ query: { campaignId }, withCredentials: true });

    socket.on('connect', () => set({ state: 'connected' }));
    socket.on('disconnect', () => set({ state: 'disconnected' }));

    socket.on(
      'campaign:snapshot',
      (snapshot: {
        audience?: string[] | string;
        roster: RosterEntry[];
        openProposal?: {
          proposalId: string;
          kind: string;
          deadlineTs: number;
          subjectCharacterId?: string | null;
          voted?: readonly string[];
          summary?: string;
        };
        worldState?: {
          gameTime?: { elapsedRounds: number; day?: number; timeOfDay?: string };
          partyLocation?: string;
          story?: string;
        };
        mode?: { inputLocked?: boolean; mapMode?: MapMode };
        map?: MapSurface;
        revealed?: string[];
        surroundings?: string[];
        sceneFeatures?: SceneFeature[];
        timeSkip?: { band: string; label: string };
        combat?: {
          round: number;
          phase: CombatView['phase'];
          deadlineTs?: number;
          combatants: CombatantView[];
          declared: string[];
          initiative?: Record<string, number>;
          mapScaleFeetPerPercent?: number;
        };
        transcript: { id: string; author: string; text: string; kind: RenderedMessage['kind']; seq: number; createdAt: number; voided?: boolean }[];
        objections?: {
          objectionId: string;
          messageId: string;
          stage: 'grounds' | 'voting';
          appellant: string;
          beat: string;
          reason?: string;
          stageDeadlineTs: number;
        }[];
        digest?: {
          sinceSeq: number;
          lines: { author: string; text: string }[];
          omitted: number;
          atLeast?: boolean;
        };
      }) => {
        // A reconnect replaces the transcript wholesale rather than merging:
        // the snapshot is already filtered to this player, and merging risks
        // keeping a line the server has since decided they may not see. Nothing
        // arrives ahead of it — the server holds a connection's events until
        // its snapshot is sent and replays them behind it (`registry.release`).
        set({
          // **An objection in progress survives the reload.** Without this the
          // vote panel was drawn only for whoever was connected when it opened,
          // while the tally counted every present player — so a reloading
          // player was silently unable to vote in a majority they were part of
          // the denominator of.
          openObjection: snapshot.objections?.at(-1) ? { ...snapshot.objections.at(-1)! } : null,
          roster: snapshot.roster,
          // **A vote is state, like fog.** Without this a player who connects a
          // minute after the announcement sees a table voting and no way in.
          openProposal: snapshot.openProposal
            ? {
                proposalId: snapshot.openProposal.proposalId,
                kind: snapshot.openProposal.kind,
                deadlineTs: snapshot.openProposal.deadlineTs,
                ...(snapshot.openProposal.summary ? { summary: snapshot.openProposal.summary } : {}),
                ...(snapshot.openProposal.subjectCharacterId
                  ? { subjectCharacterId: snapshot.openProposal.subjectCharacterId }
                  : {}),
              }
            : null,
          ...(Array.isArray(snapshot.audience) && typeof snapshot.audience[0] === 'string'
            ? { myCharacterId: snapshot.audience[0] }
            : {}),
          clock: snapshot.worldState?.gameTime ?? null,
          inputLocked: snapshot.mode?.inputLocked ?? false,
          mapMode: snapshot.mode?.mapMode ?? 'theatre',
          map: snapshot.map ?? {},
          partyLocation: snapshot.worldState?.partyLocation ?? null,
          story: snapshot.worldState?.story ?? null,
          // Fog is state, so it arrives here as well as on `fog:update` — a
          // player who joins after the last reveal would otherwise see an
          // empty map and an event that will never repeat itself.
          revealed: snapshot.revealed ?? [],
          surroundings: snapshot.surroundings ?? [],
          sceneFeatures: snapshot.sceneFeatures ?? [],
          timeSkip: snapshot.timeSkip ?? null,
          // **Absent means nothing to catch up on**, and is set back to null
          // rather than left alone: a reconnect that finds nothing missed must
          // clear the panel the previous one raised.
          digest: snapshot.digest ?? null,
          // Rebuilt wholesale, like the transcript: the server has just told
          // us the whole truth about the fight, including which of our own
          // declarations already landed.
          combat: snapshot.combat
            ? {
                round: snapshot.combat.round,
                phase: snapshot.combat.phase,
                deadlineTs: snapshot.combat.deadlineTs,
                combatants: snapshot.combat.combatants,
                declared: snapshot.combat.declared,
                summaries: {},
                initiative: snapshot.combat.initiative,
                mapScaleFeetPerPercent: snapshot.combat.mapScaleFeetPerPercent,
              }
            : null,
          messages: snapshot.transcript.map((m) => ({
            id: m.id,
            author: m.author,
            text: m.text,
            kind: m.kind,
            seq: m.seq,
            createdAt: m.createdAt,
            ...(m.voided ? { voided: true } : {}),
          })),
        });
      },
    );

    socket.on(
      'chat:message',
      (message: {
        id: string;
        author: string;
        text: string;
        kind: RenderedMessage['kind'];
        seq: number;
        createdAt: number;
        clientId?: string;
      }) => {
        set((s) => {
          // Replace our optimistic copy if this is the echo of it.
          const withoutPending = s.messages.filter(
            (m) => !(m.pending && m.clientId && m.clientId === message.clientId),
          );
          if (withoutPending.some((m) => m.id === message.id)) return s;
          return {
            messages: sortBySeq([
              ...withoutPending,
              {
                id: message.id,
                author: message.author,
                text: message.text,
                kind: message.kind,
                seq: message.seq,
                createdAt: message.createdAt,
              },
            ]),
          };
        });
      },
    );

    // The full list, which is what carries names. Replaces wholesale rather
    // than merging: the server has just told us the whole truth.
    socket.on(
      'proposal:open',
      (p: {
        proposalId: string;
        kind: string;
        deadlineTs: number;
        subjectCharacterId?: string;
        summary?: string;
      }) => {
        set({
          openProposal: {
            proposalId: p.proposalId,
            kind: p.kind,
            deadlineTs: p.deadlineTs,
            ...(p.summary ? { summary: p.summary } : {}),
            ...(p.subjectCharacterId ? { subjectCharacterId: p.subjectCharacterId } : {}),
          },
        });
      },
    );

    socket.on('roster:update', (update: { players: RosterEntry[] }) => {
      set((s) => ({
        roster: update.players,
        // The party panel and the combat panel show the same hit points from
        // two events. Keeping them in step here is cheaper than letting a
        // player read two different numbers off one screen.
        combat: s.combat
          ? combatantsUpdated(
              s.combat,
              s.combat.combatants.map((c) => {
                const player = update.players.find((p) => p.characterId === c.characterId);
                return player
                  ? {
                      ...c,
                      hitPoints: player.hitPoints.current,
                      maxHitPoints: player.hitPoints.max,
                      conscious: player.hitPoints.current > 0,
                    }
                  : c;
              }),
            )
          : null,
      }));
    });

    // The narrow event, for a fast dot update between full refreshes. It
    // cannot add a player — it only adjusts one already on the list, because
    // it carries no name to show.
    socket.on('presence:update', (update: { characterId: string; state: RosterEntry['presence'] }) => {
      set((s) => ({
        roster: s.roster.map((r) =>
          r.characterId === update.characterId ? { ...r, presence: update.state } : r,
        ),
      }));
    });

    socket.on(
      'engine:result',
      (result: {
        seq: number;
        actionId?: string;
        summary?: string;
        rolls?: { raw: number[]; total: number }[];
      }) => {
        const detail = result.rolls?.[0];
        // The engine's own sentence when it sent one ("Ilra Fenn rolled 1d20: 15"),
        // which is what the transcript stores too. A roll from an older server
        // falls back to its faces; anything else is a kind this client does not
        // render yet, and inventing a line for it would be worse than showing none.
        const text =
          result.summary ?? (detail ? `${detail.raw.join(', ')} = ${detail.total}` : undefined);
        if (!text) return;

        set((s) => ({
          messages: sortBySeq([
            ...s.messages,
            {
              // Every live engine line carries the same sentinel seq, so the
              // seq cannot identify one. The actionId does, when there is one.
              id: result.actionId ?? `engine-${(liveLineCounter += 1)}`,
              // Matches the author the server persists, so the line reads the
              // same live and after a reload.
              author: 'engine',
              text,
              kind: 'system',
              seq: result.seq,
              // Its id is synthetic — see `RenderedMessage.live`. The same
              // line reloads with a real one, and is objectable then.
              live: true,
            },
          ]),
        }));
      },
    );

    // The clock moves by events, so it cannot wait for the next snapshot —
    // and seeing it move is what makes a verb's time cost real rather than a
    // number on a button.
    // "Aragorn is searching the desk…", while it resolves. The server sends
    // one only for a *surfaced* attempt: an ambient or concealed one sends
    // nothing at all, so there is nothing here that could leak.
    socket.on(
      'character:update',
      (update: {
        characterId?: string;
        patch?: {
          experienceAwarded?: number;
          levels?: { class: string; level: number }[];
          /** Free spells a wizard's level-up earned, and has yet to choose. */
          spellsToLearn?: number;
        };
      }) => {
        const levels = (update.patch?.levels ?? [])
          .map((l) => `${l.class} ${l.level}`)
          .join(', ');
        const xp = update.patch?.experienceAwarded;
        // **Whose sheet.** A patron receives their retainer's patches too — a
        // henchman has no connection, so the event is addressed to the patron —
        // and reading "your sheet: fighter 2" when it was Wick who levelled is a
        // wrong statement about your own character, not merely a vague one.
        const mine = !update.characterId || update.characterId === get().myCharacterId;
        const whose = mine ? 'your sheet' : 'your henchman’s sheet';
        // **The free spell is a choice, so it is said out loud.** The Spells
        // panel offers it, but the panel is one tab of several and a level-up
        // notice that mentioned only hit points would leave the pick sitting
        // there unmade — the counter is the only thing that knows it is owed.
        const toLearn = update.patch?.spellsToLearn;
        const learn = toLearn ? ` · ${toLearn} new spell${toLearn > 1 ? 's' : ''} to learn` : '';
        set((current) => ({
          sheetVersion: current.sheetVersion + 1,
          sheetNotice:
            levels !== ''
              ? `${whose}: ${levels}${xp ? ` (+${xp} xp)` : ''}${learn}`
              : xp
                ? `${whose}: +${xp} xp${learn}`
                : `${whose} changed${learn}`,
        }));
      },
    );

    socket.on(
      'zone:update',
      (update: {
        zones?: { byDefender?: Record<string, Record<string, string>> };
        combatants?: CombatantView[];
        mapScaleFeetPerPercent?: number;
      }) => {
        set((s) => {
          if (!s.combat) return {};
          // The combatants ride along because framing can change *them* — the
          // cover they are behind, the water they are standing in. Applied
          // through `combatantsUpdated` rather than by rebuilding the view, so
          // the phase and the declarations already made survive it.
          const withZones = { ...s.combat, zones: update.zones?.byDefender ?? {} };
          return {
            combat: update.combatants
              ? combatantsUpdated(withZones, update.combatants, update.mapScaleFeetPerPercent)
              : withZones,
          };
        });
      },
    );

    // Both presses land here: the accepted one arrives beside its room-wide
    // system line, and the one inside the cooldown arrives alone.
    socket.on('veto:ack', (ack?: { undone?: boolean }) => {
      // An undo is answered to the presser's own socket alone; a press arrives on its own.
      if (ack?.undone === true) set({ vetoNotice: 'Undone — the DM may describe that again.' });
      else if (ack?.undone === false) set({ vetoNotice: 'Nothing to undo.' });
      else set({ vetoNotice: 'Noted. The scene moves on.' });
    });

    // **Flat, not `{ payload }`.** `emitToAudience` spreads the payload and adds
    // `seq` and `audience`, exactly as every other listener here reads it.
    // Taking `p.payload` yields `undefined`, and `{ ...undefined }` is `{}` —
    // *truthy*, so the panel rendered with a blank name and no `objectionId`,
    // and every vote was refused for a missing field. Typechecked, built, and
    // wrong; only a browser could show it.
    socket.on(
      'objection:open',
      (p: {
        objectionId: string;
        messageId: string;
        appellant: string;
        stageDeadlineTs: number;
      }) => {
        set({
          openObjection: {
            objectionId: p.objectionId,
            messageId: p.messageId,
            stage: 'grounds',
            appellant: p.appellant,
            stageDeadlineTs: p.stageDeadlineTs,
          },
          objectionNotice: null,
        });
      },
    );

    // Stage 2 resolved (submitted, or the sweeper's own empty-grounds
    // fallback) — Stage 3's vote begins. **No Sustain/Overrule panel before
    // this fires**: voting during `'grounds'` is refused server-side, a
    // confusing message for a vote that has not started.
    socket.on(
      'objection:grounds',
      (p: {
        objectionId: string;
        messageId: string;
        appellant: string;
        beat: string;
        grounds?: string;
        stageDeadlineTs: number;
      }) => {
        set((s) => ({
          openObjection:
            s.openObjection?.objectionId === p.objectionId
              ? {
                  ...s.openObjection,
                  stage: 'voting',
                  beat: p.beat,
                  ...(p.grounds ? { reason: p.grounds } : {}),
                  stageDeadlineTs: p.stageDeadlineTs,
                }
              : s.openObjection,
        }));
      },
    );

    socket.on(
      'objection:closed',
      (p: {
        objectionId: string;
        messageId: string;
        outcome: 'sustained' | 'overruled';
        sustainVotes: number;
        overruleVotes: number;
        present: number;
      }) => {
        const { objectionId, messageId, outcome, sustainVotes, present } = p;
        set((s) => ({
          // **Cleared only if it is the one on screen.** Clearing
          // unconditionally takes the panel down for an objection that is
          // still open, which a client can hold whenever its snapshot and a
          // live `objection:open` disagree about which one that is.
          openObjection: s.openObjection?.objectionId === objectionId ? null : s.openObjection,
          // **The beat is marked where it is read.** A set-aside beat that
          // renders identically to a standing one makes the whole promise —
          // that the record of a disputed call survives — invisible, and the
          // Object link would be offered on it again.
          messages: s.messages.map((m) => (m.id === messageId && outcome === 'sustained' ? { ...m, voided: true } : m)),
          // **Said here as well as in the transcript.** The room-wide system
          // line carries the result too, but a player who voted is owed an
          // answer where they pressed — the `veto:ack` lesson.
          objectionNotice:
            outcome === 'sustained'
              ? `The table set it aside — ${sustainVotes} of ${present}.`
              : `The objection did not carry — ${sustainVotes} of ${present}. What happened stands.`,
        }));
      },
    );

    // Scenery, room-wide. The whole set every time, so an empty list clears
    // it — taking the party out of the caves is saying something.
    socket.on('state:features', (update: { features?: SceneFeature[] }) => {
      set({ sceneFeatures: update.features ?? [] });
    });

    socket.on('state:surroundings', (update: { surroundings?: string[] }) => {
      set({ surroundings: update.surroundings ?? [] });
    });

    socket.on('fog:update', (update: { revealed?: string[] }) => {
      set({ revealed: update.revealed ?? [] });
    });

    // Replaces the surface wholesale rather than patching it, so a client that
    // missed one converges anyway — and so clearing a map is expressible.
    socket.on('map:update', (update: MapSurface) => {
      set({
        map: {
          ...(update.worldMapUrl ? { worldMapUrl: update.worldMapUrl } : {}),
          ...(update.encounterMapUrl ? { encounterMapUrl: update.encounterMapUrl } : {}),
        },
      });
    });

    socket.on('action:pending', (pending: { summary?: string }) => {
      set({ pending: pending.summary ?? null });
    });

    socket.on(
      'world:update',
      (update: {
        gameTime?: { elapsedRounds: number; day?: number; timeOfDay?: string };
        partyLocation?: string;
      }) => {
        // Both fields are optional and either can arrive alone, so neither may
        // clear the other by being absent.
        if (update.gameTime) set({ clock: update.gameTime });
        if (update.partyLocation) set({ partyLocation: update.partyLocation });
      },
    );

    // --- Combat ------------------------------------------------------------
    //
    // Eight events in order make one picture. None of them is a turn queue:
    // everyone declares in parallel and initiative is rolled per side after
    // the window closes.

    socket.on(
      'state:mode',
      (mode: { interactionMode?: string; inputLocked?: boolean; mapMode?: MapMode }) => {
        set({
          inputLocked: mode.inputLocked ?? false,
          ...(mode.mapMode ? { mapMode: mode.mapMode } : {}),
        });
        // Leaving combat clears the panel; the closing summary arrives on
        // combat:end just before this and is shown from the transcript.
        if (mode.interactionMode === 'exploration') set({ combat: null });
      },
    );

    socket.on(
      'combat:start',
      (start: { round: number; combatants: CombatantView[] }) => {
        set((s) => ({ combat: combatStarted(start.round, start.combatants, s.combat ?? undefined) }));
      },
    );

    socket.on('round:begin', (begin: { round: number }) => {
      set((s) => (s.combat ? { combat: roundBegan(s.combat, begin.round) } : {}));
    });

    socket.on('declare:open', (open: { round: number; deadlineTs?: number }) => {
      set((s) => (s.combat ? { combat: windowOpened(s.combat, open.round, open.deadlineTs) } : {}));
    });

    socket.on('declare:ack', (ack: { combatantId: string; summary: string }) => {
      set((s) => (s.combat ? { combat: declarationAcked(s.combat, ack.combatantId, ack.summary) } : {}));
    });

    socket.on(
      'declare:close',
      (close: { round: number; initiative: Record<string, number> }) => {
        set((s) => (s.combat ? { combat: windowClosed(s.combat, close.round, close.initiative) } : {}));
      },
    );

    socket.on('round:advance', (advance: { round: number }) => {
      set((s) => (s.combat ? { combat: roundBegan(s.combat, advance.round) } : {}));
    });

    socket.on('combat:end', (end: { outcome: string; xpAwarded: number }) => {
      set((s) => (s.combat ? { combat: combatEnded(s.combat, end.outcome, end.xpAwarded) } : {}));
    });

    // --- Narration ---------------------------------------------------------
    //
    // The engine result above is the truth and is already persisted; all of
    // this decorates it. Every failure state here is one play continues
    // through, which is why none of them clears the transcript.

    socket.on('dm:typing', (update: { on: boolean }) => {
      set((s) => ({
        // A new beat clears the last one's status: a degraded ladder or a dead
        // provider is a fact about one narration, and leaving it up would make
        // a recovered DM look permanently broken.
        dm: update.on ? { typing: true } : { ...s.dm, typing: false, queuedAt: undefined },
        // The pending line covers the wait for prose, so it ends when the
        // prose does.
        ...(update.on ? {} : { pending: null }),
        // The preview goes when the stream stops. `dm:done` re-inserts the
        // line from the text the server actually stored; a narration that was
        // cancelled sends no `dm:done`, and then there is nothing to insert —
        // which is the point.
        messages: update.on ? s.messages : dropLiveNarration(s.messages),
      }));
    });

    socket.on('dm:chunk', (chunk: { text: string }) => {
      set((s) => ({ messages: appendNarrationChunk(s.messages, chunk.text) }));
    });

    socket.on('dm:done', (done: { messageId: string; text: string; seq: number; createdAt: number }) => {
      set((s) => ({
        messages: commitNarration(s.messages, {
          id: done.messageId,
          text: done.text,
          seq: done.seq,
          createdAt: done.createdAt,
        }),
        dm: { ...s.dm, typing: false, queuedAt: undefined },
      }));
    });

    socket.on('dm:queued', (queued: { position: number }) => {
      set((s) => ({ dm: { ...s.dm, queuedAt: queued.position } }));
    });

    socket.on('dm:degraded', (degraded: { mode: NonNullable<DmStatus['degraded']> }) => {
      set((s) => ({ dm: { ...s.dm, degraded: degraded.mode } }));
    });

    socket.on('dm:unavailable', (down: { reason: NonNullable<DmStatus['unavailable']> }) => {
      set((s) => ({
        dm: { ...s.dm, typing: false, queuedAt: undefined, unavailable: down.reason },
        messages: dropLiveNarration(s.messages),
        pending: null,
      }));
    });

    socket.on('error:event', (error: { event: string; reason: string }) => {
      // **A claim refusal has its own inline place to land** — the Stage-1
      // dialog itself — so it skips the generic banner rather than showing
      // the same "too late" message twice.
      if (error.event === 'player:object_claim') {
        set({ objectionClaim: { ok: false, reason: error.reason } });
        return;
      }
      // A refusal ends any wait it was part of: a pending line nothing will
      // ever answer is worse than none.
      //
      // **The reason alone.** The event name is the wire's, not the player's:
      // a table reading "spell:memorise: no free level-1 wizard slot" is being
      // shown the protocol. The server's own words are already written for a
      // human — that is why they are forwarded at all.
      set({ lastError: error.reason, pending: null });
    });

    socket.on('objection:claim_ack', () => {
      set({ objectionClaim: { ok: true } });
    });
  },

  disconnect: () => {
    socket?.disconnect();
    socket = null;
    set({
      state: 'idle',
      campaignId: null,
      messages: [],
      roster: [],
      dm: NO_DM_ACTIVITY,
      clock: null,
      mapMode: 'theatre',
      map: {},
      partyLocation: null,
      story: null,
      digest: null,
      revealed: [],
      sheetNotice: null,
      pending: null,
      combat: null,
      myCharacterId: null,
      inputLocked: false,
    });
  },

  speakCant: (text) => {
    if (!socket) return;
    const trimmed = text.trim();
    if (trimmed === '') return;
    // **No optimistic line**, for the same reason a whisper has none: the
    // audience is the server's to decide, and a locally-rendered copy would
    // show the speaker a line before anyone knows who may read it.
    socket.emit('chat:cant', { campaignId: get().campaignId, text: trimmed });
  },

  whisper: (text, to) => {
    const trimmed = text.trim();
    if (!trimmed || !socket || to.length === 0) return;
    // **No optimistic line.** A whisper's audience is the server's to decide —
    // it re-checks that every named character is at this table — and echoing
    // it here would show the sender a message that may never have been sent.
    socket.emit('chat:whisper', { campaignId: get().campaignId, text: trimmed, audience: [...to] });
  },

  raiseVeto: () => {
    socket?.emit('player:veto', { campaignId: get().campaignId });
  },

  undoVeto: () => {
    socket?.emit('player:veto', { campaignId: get().campaignId, undo: true });
  },


  propose: (kind, payload, subjectCharacterId) => {
    socket?.emit('player:propose', {
      campaignId: get().campaignId,
      kind,
      ...(payload ? { payload } : {}),
      ...(subjectCharacterId ? { subjectCharacterId } : {}),
    });
  },

  /**
   * **No optimistic tally.** The server is the only thing that counts votes;
   * marking it locally just stops the same player being offered the buttons
   * again, and a reload correctly re-offers them because a second vote from one
   * character overwrites the first rather than adding a voice.
   */
  voteOnProposal: (inFavour) => {
    const open = get().openProposal;
    if (!socket || !open) return;
    socket.emit('player:vote_proposal', {
      campaignId: get().campaignId,
      proposalId: open.proposalId,
      inFavour,
    });
    set({ openProposal: { ...open, voted: true } });
  },

  /**
   * Ask the DM for a beat.
   *
   * **Any player's.** The text is quoted to the model as a request and never
   * executed as an instruction, so this sets no state and decides no outcome —
   * so it belongs to nobody in particular. The server rate-limits it, because
   * every prompt is a model call.
   */
  promptDm: (text) => {
    const trimmed = text.trim();
    if (!trimmed || !socket) return;
    socket.emit('player:prompt_dm', { campaignId: get().campaignId, text: trimmed });
  },

  raiseObjection: (messageId) => {
    if (!socket || !messageId) return;
    socket.emit('player:object', { campaignId: get().campaignId, messageId });
  },

  claimObjectionLink: (messageId) => {
    if (!socket || !messageId) return;
    socket.emit('player:object_claim', { campaignId: get().campaignId, messageId });
  },

  submitObjectionGrounds: (objectionId, grounds) => {
    if (!socket) return;
    socket.emit('player:object_grounds', {
      campaignId: get().campaignId,
      objectionId,
      ...(grounds?.trim() ? { grounds: grounds.trim() } : {}),
    });
  },

  voteOnObjection: (sustain) => {
    const objection = get().openObjection;
    if (!socket || !objection) return;
    socket.emit('player:vote_objection', { campaignId: get().campaignId, objectionId: objection.objectionId, sustain });
    // **Marked locally, and only locally.** The server is the only thing that
    // counts votes; this just stops the same player being offered the buttons
    // again, and a reload correctly re-offers them because a second vote from
    // one character overwrites the first rather than adding a voice.
    set({ openObjection: { ...objection, voted: true } });
  },

  say: (text) => {
    const trimmed = text.trim();
    if (!trimmed || !socket) return;

    const clientId = crypto.randomUUID();
    set((s) => ({
      messages: sortBySeq([
        ...s.messages,
        { id: clientId, clientId, author: 'you', text: trimmed, kind: 'player', pending: true },
      ]),
    }));

    socket.emit('chat:say', { campaignId: get().campaignId, text: trimmed, clientId });
  },

  /**
   * Submits one exploration action.
   *
   * No optimistic line: an action is a request to the engine, not a message,
   * and what comes back is the engine's `engine:result` rather than an echo of
   * what was asked. Showing a hopeful copy of it would mean rendering an
   * outcome the server has not decided.
   *
   * `actionId` is generated here because the director dedupes on it — a
   * double-click or a reconnect replay is then answered with the original
   * outcome instead of resolving twice.
   */
  submitAction: (verb, text, targetId, disposition) => {
    if (!socket) return;
    socket.emit('action:submit', {
      campaignId: get().campaignId,
      actionId: crypto.randomUUID(),
      verb,
      // Elaboration only. The server never parses it for intent, and neither
      // does this — the verb is the whole of what was declared.
      text: text.trim(),
      ...(targetId ? { targetId } : {}),
      ...(disposition ? { disposition } : {}),
    });
    set({ lastError: null });
  },

  /**
   * A combat declaration.
   *
   * The same socket event as an exploration action, deliberately: the *server*
   * decides which closed set applies from the mode it holds, and a client that
   * chose would be able to declare an attack out of combat.
   */
  declareCombat: ({
    action,
    targetId,
    weapon,
    spell,
    spellMode,
    spellAnimals,
    reversed,
    blessedWeapon,
    characterId,
    range,
    holdingBack,
    feature,
    destination,
  }) => {
    if (!socket) return;
    socket.emit('action:submit', {
      campaignId: get().campaignId,
      actionId: crypto.randomUUID(),
      verb: action,
      text: '',
      ...(targetId ? { targetId } : {}),
      ...(weapon ? { weapon } : {}),
      // **Only ever a henchman of the sender's own character.** Sent because a
      // retainer has no connection to declare through; the ownership test is
      // the server's, because a `characterId` on the wire is otherwise a player
      // choosing whose turn to take.
      ...(characterId ? { characterId } : {}),
      // **The band, not the penalty.** The figures are the table's
      // (`missileMediumRange` -2, `missileLongRange` -5) and reached no attack
      // at all until 2026-08-14; the engine has no coordinates, so the shooter
      // says how far and the server prices it.
      ...(range && range !== 'short' ? { range } : {}),
      // **Only when true.** Not holding back is the book's baseline, so an
      // explicit `false` says nothing the default does not already say.
      ...(holdingBack === true ? { holdingBack: true } : {}),
      // The name only. Casting time is added to initiative, so the server
      // reads it from the spell rather than believing a number from here.
      ...(spell ? { spell } : {}),
      // An id, only sent when the picker actually offered one — a spell
      // with no modes never puts this on the wire at all.
      ...(spellMode ? { spellMode } : {}),
      // Names only; the server checks each against the bestiary and the spell.
      ...(spellAnimals && spellAnimals.length > 0 ? { spellAnimals: [...spellAnimals] } : {}),
      // **Only ever true, never false.** The forward cast is the book's own
      // default reading of a spell's name, so an absent flag and an
      // explicit `false` are the same declaration.
      ...(reversed === true ? { reversed: true } : {}),
      ...(blessedWeapon ? { blessedWeapon } : {}),
      // ⚠ **Sent whenever it was decided, including as `''`.** Empty means
      // "step out into the open", which is a real declaration; omitting the key
      // leaves this character wherever they already were. A truthiness test
      // here would collapse the two and make one unexpressible.
      ...(feature !== undefined ? { feature } : {}),
      ...(destination ? { destination } : {}),
    });
    set({ lastError: null });
  },

  memoriseSpells: (entries, characterId) => {
    socket?.emit('spell:memorise', {
      campaignId: get().campaignId,
      entries,
      // A retainer of the sender's own character, re-checked server-side. A
      // henchman has no login, so this is the only way their slots refill.
      ...(characterId ? { characterId } : {}),
    });
    set({ lastError: null });
  },

  castSpell: (name, targetId, characterId) => {
    socket?.emit('spell:cast', {
      campaignId: get().campaignId,
      actionId: crypto.randomUUID(),
      name,
      ...(targetId ? { targetId } : {}),
      ...(characterId ? { characterId } : {}),
    });
    set({ lastError: null });
  },

  /**
   * The player's own notes.
   *
   * Private by construction: the row is keyed on the writer's own character
   * and there is no shape in the contract that could name anyone else's. The
   * DM has no read access either.
   */
  saveNotes: (text) => {
    socket?.emit('notes:save', { campaignId: get().campaignId, text });
  },

  hostCommand: (event, payload = {}) => {
    socket?.emit(event, { campaignId: get().campaignId, ...payload });
    set({ lastError: null });
  },

  rollDice: (sides, count = 1, isPrivate = false) => {
    socket?.emit('dice:roll', {
      campaignId: get().campaignId,
      die: { sides, count },
      count,
      private: isPrivate,
    });
  },

  rollCheck: (what, isBlind = false, asGroup = false) => {
    socket?.emit('skill:check', {
      campaignId: get().campaignId,
      ...what,
      private: isBlind,
      // Sent only when asked for. The server picks the roller, so the field is
      // the whole request — there is nobody to name.
      ...(asGroup ? { group: true } : {}),
    });
    set({ lastError: null });
  },

  setPresence: (state) => {
    socket?.emit('presence:set', { campaignId: get().campaignId, state });
  },
}));
